import classnames from "classnames";
import dynamic from "next/dynamic";
import React, { PropsWithChildren, useCallback, useState } from "react";
import { FocusedInputShape } from "react-dates";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import { makeStyles } from "@mui/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import ArrowForward from "@mui/icons-material/ArrowForward";
import CalendarToday from "@mui/icons-material/CalendarToday";
import People from "@mui/icons-material/People";
import Room from "@mui/icons-material/Room";
import Button from "@mui/material/Button";
import moment from "moment";
import { useIsMobile } from "../../../hooks/hooks";
import FHButton from "../FHButton";
import { ThemeProvider } from "@mui/material/styles";
import theme from "src/themes/SemanticTheme";

const useStyles = makeStyles((theme) => ({
  root: (props: any) => ({
    padding: props.isBookNowWidget ? 0 : theme.spacing(2),
    boxSizing: "border-box",
    zIndex: 5,
    ...(props.isBookNowWidget && { boxShadow: "none" }),

    [theme.breakpoints.up("md")]: {
      position: "relative",
      padding: props.isBookNowWidget ? 0 : theme.spacing(2),
    },

    "&$column": {
      padding: theme.spacing(2),

      "& .MuiGrid-item": {
        flexBasis: "100%",
      },
    },
    "& button": {
      "& svg": {
        verticalAlign: "middle",
      },
    },
  }),
  column: {},
  row: {},
  dateDivider: {
    paddingLeft: theme.spacing(0.5),
    marginLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
    borderLeft: `2px solid ${theme.palette.divider}`,
    borderRight: `2px solid ${theme.palette.divider}`,
  },
  divider: (props: any) => ({
    "&::before": {
      content: '""',
      display: "block",
      borderBottom: `2px solid ${theme.palette.divider}`,
      marginTop: theme.spacing(0.9),
      marginBottom: theme.spacing(0.9),
      minHeight: 0,

      [theme.breakpoints.up("md")]: {
        ...(!props.column &&
          !props.isBookNowWidget && {
            minHeight: 60,
            borderBottom: 0,
            margin: 0,
            borderRight: `2px solid ${theme.palette.divider}`,
          }),
      },
    },
  }),
  searchFormFields: (props: any) => ({
    fontWeight: props?.reduceDropdownButtonInactiveWeight ? 500 : 600,
    boxShadow: props?.bookingWidgetDropdownBtnShadow
      ? "0px 0px 10px 0px rgba(0,0,0,0.1)"
      : undefined,
    border: "2px solid #EBEBEB",
    padding: theme.spacing(0.8),
    "& span:first-child": {
      marginLeft: 0,
      "& >span": {
        fontSize: 16,
        lineHeight: "10px",
      },
      "& svg": {
        fontSize: 16,
        height: 16,
        width: 16,
      },
    },
  }),
  searchAlignTitles: (props: any) => ({
    justifyContent: "flex-start",
    paddingLeft: theme.spacing(1.6),
  }),
  activeSearchFormField: (props: any) => ({
    border: `2px solid ${theme.palette.primary.main}`,
  }),
  ellipsis: {
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
  },
  mobileDrawerContent: {
    transform: "translate(0, 0)",
    display: "flex",
    height: "100%",
    justifyContent: "flex-end",
    "& .MuiCardHeader-root": {
      borderBottom: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(1.6, 1.5),
    },
    "& .MuiCardHeader-action": {
      margin: 0,
    },
    "& .MuiPaper-root": {
      padding: 0,
      height: "100%",
      width: "100%",
      backgroundColor: "transparent",
      boxShadow: "none",
    },
  },
  cardRoot: {
    [theme.breakpoints.up("md")]: {
      display: "flex",
      flexDirection: "column",
    },
  },
  closeButton: {
    color: theme.palette.secondary.main,
    borderColor: theme.palette.secondary.main,
    borderWidth: 2,
    padding: theme.spacing(0.3),
    minWidth: "auto",
    margin: theme.spacing(0, 0, 0, 1),
    "&:hover": {
      color: theme.palette.secondary.main,
      borderColor: theme.palette.secondary.main,
      borderWidth: 2,
    },
  },
  cardContentContainer: {
    position: "relative",
    height: "100%",
    padding: theme.spacing(0, 2),
  },
  cardContent: {
    padding: theme.spacing(2, 0, 0),
    width: "100%",
    height: "100%",
    [theme.breakpoints.up("md")]: {
      overflowY: "auto",
      overflowX: "hidden",
    },
  },
  checkAvailBtnBookingWidgetButtonLabels: {
    height: "0px",
    [theme.breakpoints.up("md")]: {
      height: "29px",
    },
  },
  paperRoot: {
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
  },
}));

export type LocationsDropdownItem = {
  id: string;
  name: string;
  region: string;
  country: string;
};
export type LocationsLineItems = {
  [id: string]: LocationsDropdownItem;
};
export type SortedLocation = {
  [key: string]: LocationsLineItems;
};

export type GuestType = "adults" | "children" | "infants" | "pets" | "bedrooms";

export type Guests = {
  [N in GuestType]: number;
} & { guestTotal?: number };

export type GuestLabels = {
  guestsTitleLabel?: string;
  adultsTitleLabel?: string;
  adultsSubtitleLabel?: string;
  childrenTitleLabel?: string;
  childrenSubtitleLabel?: string;
  infantsTitleLabel?: string;
  infantsSubtitleLabel?: string;
  petsTitleLabel?: string;
  petsSubtitleLabel?: string;
  cabinsTitleLabel?: string;
  bedroomsTitleLabel?: string;
  bedroomsSubTitleLabel?: string;
  wheelChairAdaptedTitleLabel?: string;
  wheelChairAdaptedSubtitleLabel?: string;
  accessibleIcon?: JSX.Element;
  multipleGuestsLabel?: string;
  singleGuestLabel?: string;
  multipleInfantsLabel?: string;
  singleInfantLabel?: string;
  multiplePetsLabel?: string;
  singlePetLabel?: string;
  accessibleLabel?: string;
};

export type GuestsConfig = {
  [N in GuestType]?: {
    min: number;
    max: number;
  };
};

export type DateRange = {
  startDateISO: string | null;
  endDateISO: string | null;
};

export type Dates = {
  dateRange: DateRange;
  focus: FocusedInputShape;
  onFocusChange(focusedInput: FocusedInputShape | null): void;
  setDateRange(date: DateRange): void;
  setFocus(focus: FocusedInputShape): void;
  currentMonth: number;
  currentYear: number;
  availableStartDates?: any;
  setCurrentYear: (year: number) => void;
  setCurrentMonth: (month: number) => void;
  calendarDatesWithCabinCount?: any;
  calendarDatesWithCabinCountLoading?: boolean;
  selectedYearFromQuickLinks?: number;
  setSelectedYearFromQuickLinks?: (selectedYear: number) => void;
  selectedMonthFromQuickLinks?: string;
  setSelectedMonthFromQuickLinks?: (selectedMonth: string) => void;
  setNights?: (nights: number) => void;
  setClearDatesAttempted?: (clear: boolean) => void;
  setApplyDatesAttempted?: (apply: boolean) => void;
};

export type BookingFormCMSLabels = {
  largeLocationLabel: string;
  locationDrawerTitle: string;
  compactLocationLabel: string;
  touchedLocationLabel?: string;
  largeGuestsLabel: string;
  compactGuestsLabel: string;
  touchedGuestLabel?: string;
  arrivalLabel: string;
  arrivalHelpText: string;
  departureLabel: string;
  departureHelpText: string;
  compactDatesLabel: string;
  locationDrawerSelectAllLabel: string;
  locationDrawerCTALabel: string;
  partyDrawerCTALabel: string;
  closeIcon?: JSX.Element;
  locationIcon?: JSX.Element;
  calendarIcon?: JSX.Element;
  partyIcon?: JSX.Element;
  legendUnavailableText?: string;
  legendAvailableText?: string;
  legendSelectedText?: string;
  legendSoldOutText?: string;
  checkAvailabilityCTALabel: string;
  selectArrivalDateLabel: string;
  selectDepartureDateLabel: string;
  helpTextIcon?: JSX.Element;
  listViewLabel: string;
  mapViewLabel: string;
  editSearchCTALabel?: string;
};

export type BookingFormProps = {
  locations: LocationsDropdownItem[];
  sortedLocations?: SortedLocation;
  listOfCheckedLocationIds?: string[];
  areAllSelected(): boolean;
  areAllSelectedInEngland?(): boolean;
  areAllSelectedInScotland?(): boolean;
  areAllSelectedInWales?(): boolean;
  handleToggle(country?: string): void;
  handleClearSelections(): void;
  handleUpdateLocation?(
    country: string,
    locationId: string,
    checked: boolean,
  ): void;
  selectedLocationIds?: string[];
  locationsLoading: boolean;
  locationsLoaded: boolean;
  guests: Guests;
  guestsConfig?: GuestsConfig;
  dda: boolean;
  dates: Dates;
  cmsLabels: BookingFormCMSLabels;
  guestLabels: GuestLabels;
  formDisabled: boolean;
  updateGuests?(type: GuestType, value: number): void;
  toggleDda?(toggled?: boolean): void;
  onUpdateLocations?(
    checkedLocationIds: string[],
    checked: boolean,
    item: LocationsDropdownItem,
  ): void;
  onToggleAllLocations?(checked: boolean): void;
  submit(): void;
  column?: boolean;
  onOpen?: (dropdown?: DropdownName) => void;
  isBookNowWidget?: boolean;
  locationMap: JSX.Element;
  hoverLocationId?: string;
  setHoverLocationId?(id: string): void;
  showSearchSummary?: boolean;
  setShowSearchSummary?: (flag: boolean) => void;
  showQuickLinks?: boolean;
  quickLinksForMonths?: {
    [key: number]: string[];
  };
  quickLinksForYears?: number[];
  showTradingPanelInCalendar?: boolean;
  tradingPanelTitle?: string;
  tradingPanelImage?: string;
  tradingPanelImageAltText?: string;
  tradingPanelMessage?: JSX.Element;
  tradingPanelLinkLabel?: string;
  tradingPanelLinkOnClick?: () => void;
  onLoad?: () => void;
  isLoading?: boolean;
  reduceDropdownButtonInactiveWeight?: boolean;
  bookingWidgetDropdownBtnShadow?: boolean;
  removeDateLabelBookingWidget?: boolean;
  saveDateSelection?: boolean;
  staticSummerMessage?: boolean;
  calendarFlexibleInput?: boolean;
  addClearCTAOnDesktop?: boolean;
  addClearCTAOnLocationGuests?: boolean;
  calendarDurationPills?: boolean;
  glenstressNaming?: boolean;
  glentressNameChange?: boolean;
  searchLeftAlignTitles?: boolean;
  setDateRange?: (date: DateRange) => void;
  setFocus?: (focus: FocusedInputShape) => void;
};

export type DropdownName = "locations" | "dates" | "guests";

const DynamicSearchSummary = dynamic(() => import("./SearchSummary"), {
  ssr: true,
});
const DynamicDatesDropdown = dynamic(() => import("./DatesDropdown"), {
  ssr: true,
});
const DynamicGuestsDropdown = dynamic(() => import("./GuestsDropdown"), {
  ssr: true,
});
const DynamicLocationsDropdown = dynamic(
  () => import("./LocationsDropdown").then((f) => f.LocationsDropdown),
  {
    ssr: true,
  },
);

export const BookingForm: React.FC<BookingFormProps> = ({
  locations,
  sortedLocations,
  listOfCheckedLocationIds,
  areAllSelected,
  areAllSelectedInEngland,
  areAllSelectedInScotland,
  areAllSelectedInWales,
  handleToggle,
  handleClearSelections,
  handleUpdateLocation,
  selectedLocationIds,
  locationsLoaded,
  locationsLoading,
  guests,
  guestsConfig,
  dda,
  dates,
  cmsLabels,
  guestLabels,
  formDisabled = false,
  updateGuests,
  toggleDda,
  onUpdateLocations,
  onToggleAllLocations,
  submit,
  column,
  onOpen,
  isBookNowWidget,
  locationMap,
  hoverLocationId,
  setHoverLocationId,
  showSearchSummary = false,
  setShowSearchSummary,
  showQuickLinks = false,
  quickLinksForMonths,
  quickLinksForYears,
  showTradingPanelInCalendar = false,
  tradingPanelTitle,
  tradingPanelImage,
  tradingPanelImageAltText,
  tradingPanelMessage,
  tradingPanelLinkLabel,
  tradingPanelLinkOnClick,
  onLoad,
  isLoading,
  reduceDropdownButtonInactiveWeight = false,
  bookingWidgetDropdownBtnShadow = false,
  removeDateLabelBookingWidget = false,
  saveDateSelection,
  staticSummerMessage,
  calendarFlexibleInput,
  addClearCTAOnDesktop,
  addClearCTAOnLocationGuests,
  calendarDurationPills,
  glenstressNaming,
  glentressNameChange,
  searchLeftAlignTitles,
  setDateRange,
  setFocus,
}: PropsWithChildren<BookingFormProps>) => {
  const isMobile = useIsMobile();
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const isBookNowWidgetMobile = isBookNowWidget && isMobile;
  const classes = useStyles({
    column,
    isBookNowWidget: isBookNowWidgetMobile,
    reduceDropdownButtonInactiveWeight: reduceDropdownButtonInactiveWeight,
    bookingWidgetDropdownBtnShadow: bookingWidgetDropdownBtnShadow,
  });
  const largeScreen = useMediaQuery("(min-width:1241px)");
  const [dropdowns, toggleDropdowns] = useState<{
    [N in DropdownName]: boolean;
  }>({
    locations: false,
    dates: false,
    guests: false,
  });

  (() => {
    if (isLoading) onLoad && onLoad();
  })();

  const closeDropdowns = useCallback(
    () =>
      toggleDropdowns({
        locations: false,
        dates: false,
        guests: false,
      }),
    [],
  );

  const toggleDropdown = useCallback((name: DropdownName) => {
    toggleDropdowns((prevDropdowns) => ({
      ...prevDropdowns,
      [name]: !prevDropdowns[name],
    }));
  }, []);

  const onSubmit = () => {
    closeDropdowns();
    setOpenDrawer(false);
    submit();
  };
  const startDate = dates.dateRange.startDateISO
    ? moment(dates.dateRange.startDateISO)
    : null;
  const endDate = dates.dateRange.endDateISO
    ? moment(dates.dateRange.endDateISO)
    : null;

  const formatDate = (date: moment.Moment): string => {
    const dateString = date.format("DD/MM/YYYY").toString();
    return dateString;
  };

  // --- FR-416 ---
  const compactDatesLabel = removeDateLabelBookingWidget
    ? "Check-in / Check-out"
    : cmsLabels?.compactDatesLabel;
  const arrivalLabel = removeDateLabelBookingWidget
    ? "Check-in"
    : cmsLabels?.arrivalLabel;
  const departureLabel = removeDateLabelBookingWidget
    ? "Check-out"
    : cmsLabels?.departureLabel;
  // --- FR-416 ---

  const renderSearchForm = React.useMemo(
    () => (
      <>
        <Grid
          container
          spacing={1.6}
          alignItems="center"
          justifyContent="space-between"
        >
          <Grid item xs={12} md={isBookNowWidgetMobile || column ? 12 : true}>
            <Button
              startIcon={
                cmsLabels.locationIcon ? cmsLabels.locationIcon : <Room />
              }
              onClick={() => toggleDropdown("locations")}
              fullWidth
              color="inherit"
              className={classnames(classes.searchFormFields, {
                [classes.activeSearchFormField]: dropdowns.locations,
                [classes.searchAlignTitles]: searchLeftAlignTitles,
              })}
            >
              {cmsLabels.touchedLocationLabel
                ? cmsLabels.touchedLocationLabel
                : largeScreen
                  ? cmsLabels.largeLocationLabel
                  : cmsLabels.compactLocationLabel}
            </Button>
          </Grid>
          <Grid item xs={12} md={isBookNowWidgetMobile || column ? 12 : 3}>
            <Button
              startIcon={
                cmsLabels.calendarIcon ? (
                  cmsLabels.calendarIcon
                ) : (
                  <CalendarToday />
                )
              }
              onClick={() => {
                toggleDropdown("dates");
                if (calendarDurationPills) {
                  setDateRange({
                    startDateISO: null,
                    endDateISO: null,
                  });
                  setFocus("startDate");
                }
              }}
              fullWidth
              color="inherit"
              className={classnames(
                classes.ellipsis,
                classes.searchFormFields,
                {
                  [classes.activeSearchFormField]: dropdowns.dates,
                  [classes.searchAlignTitles]: searchLeftAlignTitles,
                },
              )}
            >
              <span className={classes.ellipsis}>
                {column ? (
                  startDate !== null && endDate !== null ? (
                    formatDate(startDate) + " - " + formatDate(endDate)
                  ) : (
                    compactDatesLabel
                  )
                ) : largeScreen ? (
                  <>
                    {startDate !== null ? formatDate(startDate) : arrivalLabel}{" "}
                    <ArrowForward className={classes.dateDivider} />{" "}
                    {endDate !== null ? formatDate(endDate) : departureLabel}
                  </>
                ) : startDate !== null && endDate !== null ? (
                  formatDate(startDate) + " - " + formatDate(endDate)
                ) : (
                  compactDatesLabel
                )}
              </span>
            </Button>
          </Grid>

          <Grid item xs={12} md={isBookNowWidgetMobile || column ? 12 : 3}>
            <Button
              startIcon={cmsLabels.partyIcon ? cmsLabels.partyIcon : <People />}
              onClick={() => toggleDropdown("guests")}
              fullWidth
              color="inherit"
              className={classnames(classes.searchFormFields, {
                [classes.activeSearchFormField]: dropdowns.guests,
                [classes.searchAlignTitles]: searchLeftAlignTitles,
              })}
            >
              <span className={classes.ellipsis}>
                {cmsLabels.touchedGuestLabel
                  ? cmsLabels.touchedGuestLabel
                  : largeScreen
                    ? cmsLabels.largeGuestsLabel
                    : cmsLabels.compactGuestsLabel}
              </span>
            </Button>
          </Grid>
          <Grid item xs={12} md={isBookNowWidgetMobile || column ? 12 : 3}>
            <FHButton
              disableElevation
              fhSize="md"
              onClick={() => onSubmit()}
              fullWidth
              disabled={formDisabled}
            >
              {cmsLabels.checkAvailabilityCTALabel}
            </FHButton>
          </Grid>
        </Grid>
        {dropdowns.locations && (
          <ThemeProvider theme={theme}>
            <DynamicLocationsDropdown
              open={dropdowns.locations}
              close={toggleDropdown}
              onUpdateLocations={onUpdateLocations}
              onToggleAllLocations={onToggleAllLocations}
              locationItems={locations}
              selectedLocationIds={selectedLocationIds}
              column={column}
              locationDrawerSelectAllLabel={
                cmsLabels?.locationDrawerSelectAllLabel
              }
              locationsLoaded={locationsLoaded}
              locationsLoading={locationsLoading}
              closeIcon={cmsLabels?.closeIcon}
              controlsCTA={cmsLabels?.locationDrawerCTALabel}
              onOpen={onOpen}
              isBookNowWidget={isBookNowWidgetMobile}
              mobileTitle={cmsLabels?.locationDrawerTitle}
              locationMap={locationMap}
              sortedLocations={sortedLocations}
              listOfCheckedLocationIds={listOfCheckedLocationIds}
              areAllSelected={areAllSelected}
              areAllSelectedInEngland={areAllSelectedInEngland}
              areAllSelectedInScotland={areAllSelectedInScotland}
              areAllSelectedInWales={areAllSelectedInWales}
              handleToggle={(country) => handleToggle(country)}
              handleClearSelections={handleClearSelections}
              handleUpdateLocation={handleUpdateLocation}
              hoverLocationId={hoverLocationId}
              setHoverLocationId={setHoverLocationId}
              listViewLabel={cmsLabels?.listViewLabel}
              mapViewLabel={cmsLabels?.mapViewLabel}
              addClearCTAOnLocationGuests={addClearCTAOnLocationGuests}
              glenstressNaming={glenstressNaming}
              glentressNameChange={glentressNameChange}
            />
          </ThemeProvider>
        )}
        {dropdowns.guests && (
          <ThemeProvider theme={theme}>
            <DynamicGuestsDropdown
              open={dropdowns.guests}
              close={toggleDropdown}
              guests={guests}
              guestLabels={guestLabels}
              config={guestsConfig}
              dda={dda}
              updateGuests={updateGuests}
              toggleDda={toggleDda}
              column={column}
              closeIcon={cmsLabels?.closeIcon}
              controlsCTA={cmsLabels.partyDrawerCTALabel}
              isBookNowWidget={isBookNowWidgetMobile}
              mobileTitle={cmsLabels.largeGuestsLabel}
              onOpen={onOpen}
              addClearCTAOnLocationGuests={addClearCTAOnLocationGuests}
            />
          </ThemeProvider>
        )}
        {dropdowns.dates && (
          <ThemeProvider theme={theme}>
            <DynamicDatesDropdown
              saveDateSelection={saveDateSelection || calendarDurationPills}
              open={dropdowns.dates}
              close={toggleDropdown}
              {...dates}
              selectArrivalDateLabel={cmsLabels.selectArrivalDateLabel}
              selectDepartureDateLabel={cmsLabels.selectDepartureDateLabel}
              arrivalHelpText={cmsLabels.arrivalHelpText}
              departureHelpText={cmsLabels.departureHelpText}
              column={column}
              closeIcon={cmsLabels?.closeIcon}
              onOpen={onOpen}
              legendUnavailableText={cmsLabels.legendUnavailableText}
              legendAvailableText={cmsLabels.legendAvailableText}
              legendSelectedText={cmsLabels.legendSelectedText}
              legendSoldOutText={cmsLabels.legendSoldOutText}
              helpTextIcon={cmsLabels?.helpTextIcon}
              isBookNowWidget={isBookNowWidgetMobile}
              showQuickLinks={showQuickLinks}
              quickLinksForYears={quickLinksForYears}
              quickLinksForMonths={quickLinksForMonths}
              showTradingPanelInCalendar={showTradingPanelInCalendar}
              tradingPanelTitle={tradingPanelTitle}
              tradingPanelImage={tradingPanelImage}
              tradingPanelMessage={tradingPanelMessage}
              tradingPanelImageAltText={tradingPanelImageAltText}
              tradingPanelLinkLabel={tradingPanelLinkLabel}
              tradingPanelLinkOnClick={tradingPanelLinkOnClick}
              staticSummerMessage={staticSummerMessage}
              calendarFlexibleInput={calendarFlexibleInput}
              addClearCTAOnDesktop={addClearCTAOnDesktop}
              calendarDurationPills={calendarDurationPills}
            />
          </ThemeProvider>
        )}
      </>
    ),
    [
      column,
      classes.root,
      classes.column,
      classes.divider,
      classes.activeSearchFormField,
      dropdowns,
      toggleDropdown,
      formDisabled,
      cmsLabels,
      dropdowns,
    ],
  );

  const bookingWidget = () => {
    return (
      <Paper
        className={classnames(classes.root, classes.paperRoot, {
          [classes.column]: column,
        })}
        {...(!isMobile && isBookNowWidget
          ? { elevation: 0, square: true }
          : {})}
      >
        <>{renderSearchForm}</>
      </Paper>
    );
  };

  return (
    <>
      {showSearchSummary ? (
        <DynamicSearchSummary
          locations={cmsLabels?.touchedLocationLabel}
          dates={
            startDate !== null && endDate !== null
              ? formatDate(startDate) + " - " + formatDate(endDate)
              : undefined
          }
          guests={cmsLabels.touchedGuestLabel}
          editSearch={cmsLabels.editSearchCTALabel}
          onEditClick={() => setShowSearchSummary(false)}
        />
      ) : (
        <>{bookingWidget()}</>
      )}
    </>
  );
};
